import React, {useEffect, useState} from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {fetchFarms, createFarm, updateFarm, deleteFarm, Farm, CreateFarmDto, UpdateFarmDto} from "../../api/farm";
import {fetchActiveTenants, Tenant} from "../../api/tenant";
import {useAuth} from "../../contexts/AuthContext";
import {UserRole} from "../../api/user";
import {Delete as DeleteIcon, Edit as EditIcon, Add as AddIcon} from "@mui/icons-material";
import {useForm} from "../../utils/useForm";
import {FORM_VALIDATION_CONFIGS} from "../../utils/formValidation";
import FormField from "../../components/common/FormField";

const FarmListPage: React.FC = () => {
  const {t} = useTranslation();
  const {user} = useAuth();
  const [farms, setFarms] = useState<Farm[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editFarm, setEditFarm] = useState<Farm | null>(null);
  const form = useForm({
    initialValues: {
      name: "",
      location: "",
      size: 1,
      status: "active" as "active" | "inactive" | "maintenance",
      description: "",
      tenantId: "",
    },
    validationConfig: FORM_VALIDATION_CONFIGS.FARM,
    onSubmit: async (values) => {
      try {
        // For non-super-admin users, ensure tenantId is set to their tenant
        const submitData = {
          ...values,
          tenantId: isSuperAdmin ? values.tenantId : user?.tenantId || "",
        };

        if (editFarm) {
          await updateFarm(editFarm.id, submitData as UpdateFarmDto);
          showSnackbar(t("Farm updated successfully"));
        } else {
          await createFarm(submitData);
          showSnackbar(t("Farm created successfully"));
        }
        loadFarms();
        handleCloseDialog();
      } catch (err) {
        const error = err as Error;
        showSnackbar(error.message || t("An error occurred"), "error");
        throw err;
      }
    },
    onError: (errors) => {
      console.log("Validation errors:", errors);
    },
  });
  const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: "success" | "error"}>({
    open: false,
    message: "",
    severity: "success",
  });

  const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;

  // More explicit check - only show tenant dropdown for super_admin
  const shouldShowTenantDropdown = Boolean(user && user.role === UserRole.SUPER_ADMIN);

  const showSnackbar = (message: string, severity: "success" | "error" = "success") => {
    setSnackbar({open: true, message, severity});
  };

  const loadFarms = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchFarms();
      setFarms(data);
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      showSnackbar(t("Failed to load farms"), "error");
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    if (isSuperAdmin) {
      try {
        const data = await fetchActiveTenants();
        setTenants(data);
      } catch (err) {
        console.error("Failed to load tenants:", err);
      }
    }
  };

  useEffect(() => {
    loadFarms();
    loadTenants();
  }, [isSuperAdmin]);

  const handleOpenDialog = (farm?: Farm) => {
    if (farm) {
      setEditFarm(farm);
      form.setValues({
        name: farm.name,
        location: farm.location,
        size: farm.size,
        status: farm.status,
        description: farm.description || "",
        tenantId: farm.tenantId || "",
      });
    } else {
      setEditFarm(null);
      // For non-super-admin users, automatically set their tenantId
      const defaultTenantId = isSuperAdmin ? "" : user?.tenantId || "";
      form.setValues({
        name: "",
        location: "",
        size: 1,
        status: "active" as "active" | "inactive" | "maintenance",
        description: "",
        tenantId: defaultTenantId,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditFarm(null);
    form.reset();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("Are you sure you want to delete this farm?"))) {
      return;
    }
    try {
      await deleteFarm(id);
      showSnackbar(t("Farm deleted successfully"));
      loadFarms();
    } catch (err) {
      const error = err as Error;
      showSnackbar(error.message || t("Failed to delete farm"), "error");
    }
  };

  return (
    <Box sx={{p: 3}}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t("Farms")}
        </Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          {t("Add Farm")}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
      )}

      <Paper sx={{width: "100%", overflow: "hidden"}}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("Name")}</TableCell>
                <TableCell>{t("Location")}</TableCell>
                <TableCell>{t("Size (acres)")}</TableCell>
                <TableCell>{t("Status")}</TableCell>
                <TableCell>{t("Tenant")}</TableCell>
                <TableCell align="right">{t("Actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{py: 3}}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : farms.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{py: 3}}>
                    <Typography color="textSecondary">{t("No farms found")}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                farms.map((farm) => {
                  const tenantName = tenants.find((tenant) => tenant.id === farm.tenantId)?.name || "-";
                  return (
                    <TableRow key={farm.id} hover>
                      <TableCell>{farm.name}</TableCell>
                      <TableCell>{farm.location}</TableCell>
                      <TableCell>{farm.size}</TableCell>
                      <TableCell>
                        <Box
                          component="span"
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: farm.status === "active" ? "success.light" : "error.light",
                            color: "common.white",
                            fontSize: "0.75rem",
                            fontWeight: "medium",
                          }}>
                          {t(farm.status)}
                        </Box>
                      </TableCell>
                      <TableCell>{tenantName}</TableCell>
                      <TableCell align="right">
                        <IconButton color="primary" onClick={() => handleOpenDialog(farm)} size="small" sx={{mr: 1}}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton color="error" onClick={() => handleDelete(farm.id)} size="small">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={!submitting ? handleCloseDialog : undefined} maxWidth="sm" fullWidth>
        <DialogTitle>{editFarm ? t("Edit Farm") : t("Add New Farm")}</DialogTitle>
        <DialogContent dividers>
          <Box component="form" noValidate sx={{mt: 1}}>
            <FormField
              name="name"
              label={t("Farm Name")}
              value={form.values.name}
              onChange={form.handleChange("name")}
              error={form.errors.name}
              required
              autoComplete="name"
              disabled={form.isSubmitting}
            />
            <FormField
              name="location"
              label={t("Location")}
              value={form.values.location}
              onChange={form.handleChange("location")}
              error={form.errors.location}
              required
              disabled={form.isSubmitting}
            />
            <FormField
              name="size"
              label={t("Size (acres)")}
              type="number"
              value={form.values.size}
              onChange={form.handleChange("size")}
              error={form.errors.size}
              required
              min={0}
              step={0.01}
              disabled={form.isSubmitting}
            />
            <FormField
              name="status"
              label={t("Status")}
              value={form.values.status}
              onSelectChange={form.handleSelectChange("status")}
              error={form.errors.status}
              select
              required
              options={[
                {value: "active", label: t("Active")},
                {value: "inactive", label: t("Inactive")},
                {value: "maintenance", label: t("Maintenance")},
              ]}
              disabled={form.isSubmitting}
            />
            <FormField
              name="description"
              label={t("Description")}
              value={form.values.description}
              onChange={form.handleChange("description")}
              error={form.errors.description}
              multiline
              rows={3}
              disabled={form.isSubmitting}
            />
            {/* Only show tenant selection for super_admin */}
            {shouldShowTenantDropdown && (
              <FormField
                name="tenantId"
                label={t("Tenant")}
                value={form.values.tenantId}
                onSelectChange={form.handleSelectChange("tenantId")}
                error={form.errors.tenantId}
                select
                required
                options={tenants.map((tenant) => ({
                  value: tenant.id,
                  label: tenant.name,
                }))}
                disabled={form.isSubmitting}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{p: 2}}>
          <Button onClick={handleCloseDialog} disabled={form.isSubmitting} color="inherit">
            {t("Cancel")}
          </Button>
          <Button
            onClick={form.handleSubmit}
            variant="contained"
            disabled={form.isSubmitting}
            startIcon={form.isSubmitting ? <CircularProgress size={20} /> : null}>
            {form.isSubmitting ? t("Saving...") : t("Save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({...prev, open: false}))}
        anchorOrigin={{vertical: "bottom", horizontal: "right"}}>
        <Alert onClose={() => setSnackbar((prev) => ({...prev, open: false}))} severity={snackbar.severity} sx={{width: "100%"}}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FarmListPage;
